import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { selectFrom, selectOne, callRPC, insertInto, updateTable, deleteFrom } from '../../services/databaseService';
import { uploadFile, deleteFile } from '../../services/storageService';
import { ProductState, CartItem, Product } from '../../@types/product';
import { collection, query, where, getDocs } from 'firebase/firestore';
//
import { dispatch } from '../store';
import { getCurrentUser } from 'src/services/authService';

// ----------------------------------------------------------------------

const initialState: ProductState = {
  isLoading: false,
  isSaving: false,
  error: null,
  products: [],
  product: null,
  allVenuesProducts: [],
  venueProducts: [],
  venueProduct: null,
  sortBy: null,
  isOpenModal: false,
  isOpenSecondModal: false,
  filters: {
    gender: [],
    category: 'All',
    colors: [],
    priceRange: [0, 200],
    rating: '',
  },
  checkout: {
    activeStep: 0,
    cart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
  },
};

const slice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // START SAVING
    startSaving(state) {
      state.isSaving = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isSaving = false;
      state.error = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action) {
      state.isLoading = false;
      state.products = Array.isArray(action.payload) ? action.payload : action.payload.products || [];
    },

    // GET PRODUCT
    getProductSuccess(state, action) {
      state.isLoading = false;
      state.isSaving = false;
      state.product = action.payload;
    },

    // GET VENUE PRODUCTS
    getAllVenuesProductsSuccess(state, action) {
      state.isLoading = false;
      state.allVenuesProducts = Array.isArray(action.payload) ? action.payload : action.payload.products || [];
    },

    // GET VENUE PRODUCTS
    getVenueProductsSuccess(state, action) {
      state.isLoading = false;
      state.venueProducts = Array.isArray(action.payload) ? action.payload : action.payload.products || [];
    },

    // GET VENUE PRODUCT
    getVenueProductSuccess(state, action) {
      state.isLoading = false;
      state.venueProduct = action.payload;
    },

    //  SORT & FILTER PRODUCTS
    sortByProducts(state, action) {
      state.sortBy = action.payload;
    },

    filterProducts(state, action) {
      state.filters.gender = action.payload.gender;
      state.filters.category = action.payload.category;
      state.filters.colors = action.payload.colors;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
    },

     // OPEN MODAL
     openModal(state) {
      state.isOpenModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
      state.isOpenSecondModal = false;
    },

     // OPEN MODAL
     openSecondModal(state) {
      state.isOpenSecondModal = true;
    },

    // CLOSE MODAL
    closeSecondModal(state) {
      state.isOpenSecondModal = false;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart = action.payload;

      const subtotal = sum(cart.map((cartItem: CartItem) => cartItem.price * cartItem.quantity));
      const discount = cart.length === 0 ? 0 : state.checkout.discount;
      const shipping = cart.length === 0 ? 0 : state.checkout.shipping;
      const billing = cart.length === 0 ? null : state.checkout.billing;

      state.checkout.cart = cart;
      state.checkout.discount = discount;
      state.checkout.shipping = shipping;
      state.checkout.billing = billing;
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal - discount;
    },

    addCart(state, action) {
      const product = action.payload;
      const isEmptyCart = state.checkout.cart.length === 0;

      if (isEmptyCart) {
        state.checkout.cart = [...state.checkout.cart, product];
      } else {
        state.checkout.cart = state.checkout.cart.map((_product) => {
          const isExisted = _product.id === product.id;
          if (isExisted) {
            return {
              ..._product,
              quantity: _product.quantity + 1,
            };
          }
          return _product;
        });
      }
      state.checkout.cart = uniqBy([...state.checkout.cart, product], 'id');
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter((item) => item.id !== action.payload);

      state.checkout.cart = updateCart;
    },

    resetCart(state) {
      state.checkout.activeStep = 0;
      state.checkout.cart = [];
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.billing = null;
    },

    onBackStep(state) {
      state.checkout.activeStep -= 1;
    },

    onNextStep(state) {
      state.checkout.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.checkout.activeStep = goToStep;
    },

    increaseQuantity(state, action) {
      const product_id = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === product_id) {
          return {
            ...product,
            quantity: product.quantity + 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    decreaseQuantity(state, action) {
      const product_id = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === product_id) {
          return {
            ...product,
            quantity: product.quantity - 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subtotal - discount;
    },

    applyShipping(state, action) {
      const shipping = action.payload;
      state.checkout.shipping = shipping;
      state.checkout.total = state.checkout.subtotal - state.checkout.discount + shipping;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  addCart,
  resetCart,
  onGotoStep,
  onBackStep,
  onNextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  increaseQuantity,
  decreaseQuantity,
  sortByProducts,
  filterProducts,
  openModal,
  closeModal,
  openSecondModal,
  closeSecondModal,
} = slice.actions;

// ----------------------------------------------------------------------

export function getProducts() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get products with categories (returns JSONB array directly)
      const products = await callRPC<any[]>('get_products_with_categories');

      console.log('Fetched products:', products);
      // Transform to match expected format
      // RPC returns: category (name), categoryId (id), type (category type)
      const transformedProducts = products.map((product: any) => ({
        ...product,
        category: product.categoryId ? {
          id: product.categoryId,
          name: product.category,
          type: product.type,
        } : null,
      }));

      // Dispatch array directly (reducer handles both array and { products: [...] } format)
      dispatch(slice.actions.getProductsSuccess(products));
    } catch (error: any) {
      console.error('Error fetching products:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueProducts(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const products = await callRPC<any[]>('get_venue_admin_products', {
        p_venue_id: venueId,
      });

      const transformedProducts = (Array.isArray(products) ? products : []).map((product: any) => ({
        ...product,
        category: product.category_name || '',
        type: product.category_type || '',
        product_status: product.product_status ?? product.venprodstatus,
      }));

      dispatch(slice.actions.getVenueProductsSuccess({ products: transformedProducts }));
    } catch (error: any) {
      console.error('Error fetching venue products:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getAllVenuesProducts() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get all venue products with joins
      const products = await callRPC<any[]>('get_all_venue_products');

      // Transform to match expected format
      const transformedProducts = products.map((product: any) => ({
        ...product,
        venue: product.venue_id ? {
          id: product.venue_id,
          name: product.venue_name,
          photo_urls: product.venue_photo_urls,
        } : null,
        category: product.category_id ? {
          id: product.category_id,
          name: product.category_name,
          type: product.category_type,
        } : null,
      }));

      dispatch(slice.actions.getAllVenuesProductsSuccess({ products: transformedProducts }));
    } catch (error: any) {
      console.error('Error fetching all venues products:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getProduct(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const product = await selectOne<any>('products', { id: parseInt(id) }, `
        *,
        product_category!products_category_id_fkey (
          id,
          name,
          type
        )
      `);

      if (!product) {
        throw new Error('Product not found');
      }

      dispatch(slice.actions.getProductSuccess(product));
      return product;
    } catch (error: any) {
      console.error('Error fetching product:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function createProduct(data: Product) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      let photoUrl = data.photo_url || null;

      // Upload photo if provided
      if (data.avatarUrl && typeof data.avatarUrl !== 'string') {
        // Generate temporary ID for photo path (will be replaced by actual ID from RPC)
        const tempId = Date.now();
        const photoPath = `products/${tempId}/photo_${Date.now()}.${data.avatarUrl.name.split('.').pop()}`;
        await uploadFile('product-photos', photoPath, data.avatarUrl, {
          upsert: false,
        });
        photoUrl = photoPath;
      }

      // Use secure RPC function (uses auth.uid() internally)
      // Map price/saleprice to standardprice/standardsaleprice for RPC
      // Components use price/saleprice, but database uses standardprice/standardsaleprice
      const product = await callRPC<any>('create_product', {
        p_name: data.name,
        p_category_id: typeof data.category_id === 'string' ? parseInt(data.category_id) : data.category_id,
        p_standardprice: data.standardprice !== undefined ? data.standardprice : (data.price !== undefined ? data.price : 0),
        p_description: data.description || null,
        p_standardsaleprice: data.standardsaleprice !== undefined ? data.standardsaleprice : (data.saleprice !== undefined ? data.saleprice : null),
        p_sale: data.sale || false,
        p_photo_url: photoUrl,
        p_sku: data.sku || null,
        p_ncm: data.ncm || null,
        p_stock_threshold: data.stock_threshold || null,
      });

      // If photo was uploaded before RPC, update with correct path
      if (data.avatarUrl && typeof data.avatarUrl !== 'string' && product.id) {
        const correctPhotoPath = `products/${product.id}/photo_${Date.now()}.${data.avatarUrl.name.split('.').pop()}`;
        // Move/update photo with correct path
        await uploadFile('product-photos', correctPhotoPath, data.avatarUrl, {
          upsert: false,
        });
        
        // Update product with correct photo URL
        await callRPC('update_product', {
          p_product_id: product.id,
          p_photo_url: correctPhotoPath,
        });
        product.photo_url = correctPhotoPath;
      }

      // Transform RPC response to include price/saleprice for frontend compatibility
      const transformedProduct = {
        ...product,
        price: product.standardprice,
        saleprice: product.standardsaleprice,
        category_id: product.category_id?.toString() || product.product_category?.id?.toString() || '',
      };

      dispatch(slice.actions.getProductSuccess(transformedProduct));
      return transformedProduct;
    } catch (error: any) {
      console.error('Error creating product:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function createVenueProduct(data: Product[], venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function for each product (uses auth.uid() internally)
      const venueProducts = await Promise.all(
        data.map((item) =>
          callRPC<any>('create_venue_product', {
            p_product_id: item.id,
            p_venue_id: venueId,
            p_name: item.name,
            p_price: item.price,
            p_saleprice: item.saleprice || null,
            p_description: item.description || null,
            p_photo_url: item.photo_url || null,
            p_venprodstatus: 1, // Active
            p_sale: item.sale || false,
          })
        )
      );

      dispatch(slice.actions.getVenueProductSuccess(venueProducts[0]));
      return venueProducts;
    } catch (error: any) {
      console.error('Error creating venue product:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateProduct(data: Product) {
  return async () => {
    dispatch(slice.actions.startSaving());
    try {
      let photoUrl: string | undefined = undefined;

      // Upload photo if provided
      if (data.avatarUrl && typeof data.avatarUrl === 'object') {
        // Delete old photo if exists
        if (data.photo_key) {
          await deleteFile('product-photos', data.photo_key);
        }

        const photoPath = `products/${data.id}/photo_${Date.now()}.${data.avatarUrl.name.split('.').pop()}`;
        await uploadFile('product-photos', photoPath, data.avatarUrl, {
          upsert: false,
        });
        photoUrl = photoPath;
      }

      // Use secure RPC function (uses auth.uid() internally)
      // Map price/saleprice to standardprice/standardsaleprice for RPC
      // Components use price/saleprice, but database uses standardprice/standardsaleprice
      const product = await callRPC<any>('update_product', {
        p_product_id: typeof data.id === 'string' ? parseInt(data.id) : data.id,
        p_name: data.name || undefined,
        p_description: data.description !== undefined ? data.description : undefined,
        p_category_id: data.category_id ? (typeof data.category_id === 'string' ? parseInt(data.category_id) : data.category_id) : undefined,
        // Map price/saleprice to standardprice/standardsaleprice
        p_standardprice: (data.standardprice !== undefined ? data.standardprice : data.price !== undefined ? data.price : undefined),
        p_standardsaleprice: (data.standardsaleprice !== undefined ? data.standardsaleprice : data.saleprice !== undefined ? data.saleprice : undefined),
        p_sale: data.sale !== undefined ? data.sale : undefined,
        p_photo_url: photoUrl,
        p_sku: data.sku || undefined,
        p_ncm: data.ncm || undefined,
        p_stock_threshold: data.stock_threshold !== undefined ? data.stock_threshold : undefined,
      });

      // Transform RPC response to include price/saleprice for frontend compatibility
      const transformedProduct = {
        ...product,
        price: product.standardprice,
        saleprice: product.standardsaleprice,
        category_id: product.category_id?.toString() || product.product_category?.id?.toString() || '',
      };

      dispatch(slice.actions.getProductSuccess(transformedProduct));
      return transformedProduct;
    } catch (error: any) {
      console.error('Error updating product:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateVenueProduct(data: {
  id: number;
  name?: string;
  description?: string;
  price?: number;
  saleprice?: number;
  sale?: boolean;
  venprodstatus?: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function (uses auth.uid() internally)
      const product = await callRPC<any>('update_venue_product', {
        p_venue_product_id: data.id,
        p_name: data.name || undefined,
        p_description: data.description !== undefined ? data.description : undefined,
        p_price: data.price !== undefined ? data.price : undefined,
        p_saleprice: data.saleprice !== undefined ? data.saleprice : undefined,
        p_sale: data.sale !== undefined ? data.sale : undefined,
        p_venprodstatus: data.venprodstatus !== undefined ? data.venprodstatus : undefined,
      });

      dispatch(slice.actions.getVenueProductSuccess(product));
      return product;
    } catch (error: any) {
      console.error('Error updating venue product:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function uploadProductPhoto(data: {
  id: number;
  avatarUrl: File;
  photo_key?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Delete old photo if exists
      if (data.photo_key) {
        await deleteFile('product-photos', data.photo_key);
      }

      // Upload new photo
      const photoPath = `products/${data.id}/photo_${Date.now()}.${data.avatarUrl.name.split('.').pop()}`;
      await uploadFile('product-photos', photoPath, data.avatarUrl, {
        upsert: false,
      });

      // Update product with photo URL
      await updateTable('products', {
        photo_url: photoPath,
      }, { id: data.id });

      // Fetch updated product
      const product = await selectOne<any>('products', { id: data.id }, `
        *,
        product_category!products_category_id_fkey (
          id,
          name,
          type
        )
      `);

      dispatch(slice.actions.getProductSuccess(product));
      return product;
    } catch (error: any) {
      console.error('Error uploading product photo:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function syncProducts(data: Array<{ product_id: string }>, venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current user from auth (we'll need to pass this or get it from context)
      // For now, using null - should be passed from component
      const venueUserId = null; // TODO: Get from auth context

      // Use RPC function to sync venue products
      const result = await callRPC('sync_venue_products', {
        p_venue_id: venueId,
        p_venue_user_id: venueUserId || '',
        p_product_ids: data.map((item) => parseInt(item.product_id)),
      });

      // Refresh venue products
      dispatch(getVenueProducts(venueId));
      return result;
    } catch (error: any) {
      console.error('Error syncing products:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteProduct(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get product to delete photo if exists
      const product = await selectOne<any>('products', { id: parseInt(id) });
      
      if (product?.photo_url) {
        await deleteFile('product-photos', product.photo_url);
      }

      // Delete product
      await deleteFrom('products', { id: parseInt(id) });

      dispatch(slice.actions.startLoading()); // Reset loading state
    } catch (error: any) {
      console.error('Error deleting product:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteVenueProducts(data: string[], venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Delete multiple venue products
      const deletePromises = data.map((product_id) =>
        deleteFrom('venue_products', {
          id: parseInt(product_id),
          venue_id: venueId,
        })
      );

      await Promise.all(deletePromises);

      // Refresh venue products
      dispatch(getVenueProducts(venueId));
    } catch (error: any) {
      console.error('Error deleting venue products:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}